import React, {useState} from 'react';
import {
    Button,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    Paper,
    SxProps,
    Typography,
} from "@mui/material"
import {EditOutlined, MoreVert, RefreshOutlined} from "@mui/icons-material"
import CreateRowModal from "../rows/CreateRowModal"
import {TestRunJson} from "../../api/json/TestRunJson"
import {useProjectData} from "../../hooks/useProjectData"
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {TestRunRequests} from "../../api/requests/TestRunRequests"
import QualityCheckJson from "../../api/json/QualityCheckJson"
import {useToasts} from "../../hooks/useToasts"

interface TestRunActionMenuProps {
    qualityCheckJson: QualityCheckJson
    testRun: TestRunJson
    didUpdate: (testRun: TestRunJson) => void
    didDelete: (exampleId: number) => void
    iconButton?: boolean
    sx?: SxProps
}

function TestRunActionMenu({qualityCheckJson, testRun, didUpdate, didDelete, iconButton, sx}: TestRunActionMenuProps) {
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement>()

    const [running, setRunning] = useState(false)
    const [rowEditorOpen, setRowEditorOpen] = useState(false)

    const {project} = useProjectData()
    const toasts = useToasts()

    const handleRerun = () => {
        setRunning(true)
        TestRunRequests.run(qualityCheckJson.id, testRun.row.id)
            .then(testRun => {
                didUpdate(testRun)
            })
            .catch(toasts.showError)
            .finally(() => {
                setRunning(false)
                setMenuAnchor(undefined)
            })
    }

    return (
        <>
            {iconButton
                ? <IconButton size="small"
                              onClick={(event) => setMenuAnchor(event.currentTarget)}
                              sx={sx}>
                    <MoreVert fontSize="small"/>
                </IconButton>
                : <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    endIcon={<ArrowDropDownIcon/>}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    sx={sx}>
                    Actions
                </Button>
            }

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(undefined)}
            >
                <Paper variant="outlined">
                    <MenuList>
                        <MenuItem onClick={() => {
                            setMenuAnchor(undefined)
                            setRowEditorOpen(true)
                        }}>
                            <ListItemIcon>
                                <EditOutlined/>
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>Edit Row</Typography>
                            </ListItemText>
                        </MenuItem>
                        <MenuItem
                            disabled={running}
                            onClick={() => {
                                handleRerun()
                            }}>
                            <ListItemIcon>
                                {running ? <CircularProgress size={20} color="secondary"/> : <RefreshOutlined/>}
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>Re-run</Typography>
                            </ListItemText>
                        </MenuItem>
                    </MenuList>
                </Paper>
            </Menu>

            {project &&
                <CreateRowModal
                    open={rowEditorOpen}
                    onClose={() => setRowEditorOpen(false)}
                    project={project}
                    row={testRun.row}
                    didUpdate={row => {
                        const updated: TestRunJson = {...testRun}
                        updated.row = row
                        didUpdate(updated)
                    }}
                    didDelete={() => didDelete(testRun.row.id)}
                />
            }
        </>
    );
}

export default TestRunActionMenu;
